import { ActivityParticipant, ActivityParticipantData, CommandResult, MeetingInvitationStatus } from '../amorg-common'
import { ActivityType, Activity } from '../amorg-common'
import { ActivityListing } from '../amorg-common'
import { AgendaMeetingActivityListing } from '../amorg-common'
import { SurveyActivityListing } from '../amorg-common'
import { SafetyInspectionActivityListing } from '../amorg-common/DTOs/SafetyInspectionActivityListing';
import { apiPath } from "./adminClientApi";
import { queryAuthWrapper, commandAuthWrapper } from "./serverApiHelpers";

export const activitiesPath = `${apiPath}/activities`;

export const getSurveyActivities = async (type:ActivityType): Promise<SurveyActivityListing[]> => {
    const result:SurveyActivityListing[] = await queryAuthWrapper(`${activitiesPath}/surveyactivities/${type}`);
    for(const a of result){
        a.start = new Date(a.start);
        a.end = new Date(a.end);
        if(a.survey){
            a.survey.start = new Date(a.survey.start);
            a.survey.end = new Date(a.survey.end);
        }
    }
    return result;
}

export const getAgendaMeetingActivities = async (type:ActivityType): Promise<AgendaMeetingActivityListing[]> => {
    const result:AgendaMeetingActivityListing[] = await queryAuthWrapper(`${activitiesPath}/agendameetingactivities/${type}`);
    for(const a of result){
        a.start = new Date(a.start);
        a.end = new Date(a.end);
    }
    return result;
}

export const getSafetyInspectionActivities = async (): Promise<SafetyInspectionActivityListing[]> => {
    const result:SafetyInspectionActivityListing[] = await queryAuthWrapper(`${activitiesPath}/safetyinspections`);
    for(const a of result){
        a.start = new Date(a.start);
        a.end = new Date(a.end);
    }
    return result;
}

export const getActivities = async (from: Date, to: Date, activityTypes: ActivityType[]): Promise<ActivityListing[]> => {
    const results: ActivityListing[] = await queryAuthWrapper(`${activitiesPath}?from=${from.toISOString()}&to=${to.toISOString()}&activityTypes=${activityTypes.join(',')}`);
    // set dates
    return results.map((a) => { return { ...a, start: new Date(a.start), end: new Date(a.end) } });
}

export const getActivity = async (activityId: string): Promise<Activity | null> => {
    const result = await queryAuthWrapper(`${activitiesPath}/${activityId}`);
    if (result == null)
        return null;

    const data = result as Activity;
    if(data.activityType === 'WorkgroupMeeting' || data.activityType === 'AnnualWorkgroupStatus') {
        data.agendaItems = data.agendaItems.map(ai => ({
            ...ai,
            actionPoints:ai.actionPoints.map(ap => ({
                ...ap,
                deadline: ap.deadline ? new Date(ap.deadline) : null,
            }))
        }));
    }

    return { ...result, start: new Date(result.start), end: new Date(result.end) } as Activity;
}

export const getActivityOverview = async (activityId: string): Promise<ActivityListing | null> => {
    const result = await queryAuthWrapper(`${activitiesPath}/${activityId}/overview`);
    if (result == null)
        return null;

    return { ...result, start: new Date(result.start), end: new Date(result.end) } as ActivityListing;
}

export const getMeetingInvitationStatus = async (activityId: string): Promise<MeetingInvitationStatus> => {
    return await queryAuthWrapper(`${activitiesPath}/${activityId}/invitationstatus`);
}

export const sendMeetingInvitation = async (activityId: string): Promise<CommandResult> => {
    return await commandAuthWrapper(`${activitiesPath}/${activityId}/meetingInvitation`, 'POST');
}

export const createActivity = async (
    id: string,
    activityType: ActivityType,
    title: string,
    description: string,
    start: Date,
    end: Date,
    location: string): Promise<CommandResult> =>

    commandAuthWrapper(`${activitiesPath}`, 'POST',
        {
            id, activityType, title, description, start: start, end, location
        }
    );

export const updateActivity = async (
    id: string,
    title: string,
    description: string,
    start: Date,
    end: Date,
    location: string): Promise<CommandResult> =>

    commandAuthWrapper(`${activitiesPath}/${id}`, 'PUT',
        {
            title, description, start, end, location
        }
    );

export const updateActivityWorkgroup = async (
    id: string, workgroupId: string): Promise<CommandResult> =>

    commandAuthWrapper(`${activitiesPath}/${id}/workgroup`, 'PUT',
        {
            workgroupId
        }
    );

export const updateActivityOwner = async (
    id: string, ownerId: string): Promise<CommandResult> =>

    commandAuthWrapper(`${activitiesPath}/${id}/owner`, 'PUT',
        {
            ownerId
        }
    );

export const deleteActivity = async (id: string): Promise<CommandResult> =>
    commandAuthWrapper(`${activitiesPath}/${id}`, 'DELETE');


// PARTICIPANTS ---------------------------------------------------------------------------------

export const updateActivityParticipants = async (
    activityId:string,
    activityParticipants:ActivityParticipantData[]
):Promise<CommandResult> => {
    const participants:ActivityParticipant[] = activityParticipants.map(p => 
        p.type.value === 'internal' 
            ? { type:'internal', id:p.id, userId:p.type.userId }
            : { type:'external', id:p.id, name:p.name, role:p.role, email:p.email }
    );


    return commandAuthWrapper(`${activitiesPath}/${activityId}/participants`, 'POST',
        { participants });
};

// AGENDAITEMS ---------------------------------------------------------------------------------

export const createAgendaItem = async (
    id: string,
    activityId: string,
    title: string,
    description: string,
    note: string
): Promise<CommandResult> =>
    commandAuthWrapper(`${activitiesPath}/${activityId}/agenda`, 'POST',
        { id, title, description, note });

export const moveAgendaItem = async (
    id: string,
    activityId: string,
    move: 'up' | 'down' | 'in' | 'out'
): Promise<CommandResult> =>
    commandAuthWrapper(`${activitiesPath}/${activityId}/agenda/${id}/${move}`, 'PUT');

export const updateAgendaItem = async (
    id: string,
    activityId: string,
    title: string,
    description: string,
    note: string
): Promise<CommandResult> =>
    commandAuthWrapper(`${activitiesPath}/${activityId}/agenda/${id}`, 'PUT',
        { title, description, note }
    );

export const deleteAgendaItem = async (
    id: string,
    activityId: string
): Promise<CommandResult> =>
    commandAuthWrapper(`${activitiesPath}/${activityId}/agenda/${id}`, 'DELETE');

export const updateAgendaItemNote = async (
    agendaItemId: string,
    note: string
): Promise<CommandResult> =>
    commandAuthWrapper(`${activitiesPath}/agenda/${agendaItemId}/note`, 'PUT',
        { note }
    );